import type { Artist, Booking, CustomerRecord, Deposit, SessionLength, Studio, Waiver } from '@/domain/types';
import { normalizeInstagramHandle } from '@/domain/instagram';
import { customerName } from '@/utils/format';
import { depositChip, waiverChip, confirmChip } from './chips';
import type { BookingSubject, CardIdentity } from './types';

const SESSION_LENGTH_LABEL: Record<SessionLength, string> = {
  small: 'Small',
  'half-day': 'Half-day',
  'full-day': 'Full-day',
  'multi-session': 'Multi-session',
};

type NameParts = Pick<CustomerRecord, 'firstName' | 'lastName' | 'preferredName' | 'instagramHandle'>;

/** Resolve identity to name/handle/ref, honouring the no-name (handle-only) case. */
export function resolveIdentity(
  customer: NameParts | undefined,
  fallbackRef: string,
  context?: string,
): CardIdentity {
  const rawName = customer ? customerName(customer).trim() : '';
  const name = rawName.length > 0 ? rawName : null;
  const handle = normalizeInstagramHandle(customer?.instagramHandle ?? '') || null;
  return { name, handle, ref: fallbackRef, context };
}

/** Derive what to actually render from a resolved identity. */
export function identityDisplay(id: CardIdentity): {
  primary: string;
  secondary: string | null;
  isHandle: boolean;
  initials: string;
} {
  if (id.name) {
    const initials =
      id.name
        .split(' ')
        .map((p) => p[0])
        .filter(Boolean)
        .slice(0, 2)
        .join('')
        .toUpperCase() || '?';
    return { primary: id.name, secondary: id.handle ? `@${id.handle}` : null, isHandle: false, initials };
  }
  if (id.handle) {
    return { primary: `@${id.handle}`, secondary: null, isHandle: true, initials: '@' };
  }
  return { primary: id.ref, secondary: null, isHandle: false, initials: id.ref.slice(0, 2).toUpperCase() };
}

/** Map a booking + its resolved relations into the normalised card spine. */
export function bookingSubject(
  booking: Booking,
  rel: {
    customer?: CustomerRecord;
    artist?: Artist;
    studio?: Studio;
    deposit?: Deposit;
    waiver?: Waiver;
    overdue?: boolean;
    selfWith?: boolean;
    /** B3: who approved a no-deposit booking (approvedByOpsLabel). */
    approvalLabel?: string;
  },
): BookingSubject {
  return {
    kind: 'booking',
    identity: resolveIdentity(
      rel.customer,
      booking.customerId.slice(-6),
      SESSION_LENGTH_LABEL[booking.sessionLength] ?? booking.sessionLength,
    ),
    startIso: booking.start,
    artistLabel: rel.selfWith ? 'with you' : rel.artist ? `with ${rel.artist.displayName}` : undefined,
    studioLabel: rel.studio?.name,
    readiness: {
      deposit: depositChip(booking, rel.deposit, rel.approvalLabel),
      waiver: waiverChip(rel.waiver),
      confirm: confirmChip(booking, { overdue: rel.overdue }),
    },
  };
}
